import { characterData, objectData, formatObjectNameForUrl } from './dreamData';

// Generate double-encoded URL patterns and their correct mappings
export const generateRedirectMapping = () => {
  const redirects: Record<string, string> = {};

  // 1. Character page redirects - from double-encoded to single-encoded
  characterData.forEach(char => {
    const singleEncoded = encodeURIComponent(char.character);
    const doubleEncoded = encodeURIComponent(singleEncoded);
    
    if (singleEncoded !== doubleEncoded) {
      redirects[`/${doubleEncoded}`] = `/${singleEncoded}`;
    }
  });

  // 2. Object page redirects - from double-encoded to single-encoded
  objectData.forEach(obj => {
    const firstChar = obj.name.charAt(0);
    const singleEncodedChar = encodeURIComponent(firstChar);
    const doubleEncodedChar = encodeURIComponent(singleEncodedChar);
    
    const singleEncodedName = formatObjectNameForUrl(obj.name);
    const doubleEncodedName = encodeURIComponent(singleEncodedName);

    // Character double-encoded, name single-encoded
    if (singleEncodedChar !== doubleEncodedChar) {
      redirects[`/${doubleEncodedChar}/${singleEncodedName}`] = `/${singleEncodedChar}/${singleEncodedName}`;
    }

    // Character single-encoded, name double-encoded
    if (singleEncodedName !== doubleEncodedName) {
      redirects[`/${singleEncodedChar}/${doubleEncodedName}`] = `/${singleEncodedChar}/${singleEncodedName}`;
    }

    // Both character and name double-encoded
    if (singleEncodedChar !== doubleEncodedChar && singleEncodedName !== doubleEncodedName) {
      redirects[`/${doubleEncodedChar}/${doubleEncodedName}`] = `/${singleEncodedChar}/${singleEncodedName}`;
    }
  });

  return redirects;
};

// Pre-generate the mapping for faster lookups
export const redirectMapping = generateRedirectMapping();

// Helper function to check if a path needs redirect
export const getRedirectTarget = (pathname: string): string | null => {
  // Check direct mapping first
  const directMapping = redirectMapping[pathname];
  if (directMapping) return directMapping;
  
  // Check for comma-containing URLs and redirect to dash-formatted URLs
  if (pathname.includes(',') || pathname.includes('%2C')) {
    try {
      // Decode the URL to work with actual characters
      const decodedPath = decodeURIComponent(pathname);
      
      // Split the path into segments
      const segments = decodedPath.split('/').filter(Boolean);
      
      if (segments.length >= 2) {
        // Get character and object name
        const character = segments[0];
        const objectName = segments.slice(1).join('/');
        
        // Format the object name properly (replace commas with dashes)
        const formattedObjectName = objectName
          .replace(/\s+/g, '-')
          .replace(/[(),]/g, '-')
          .replace(/--+/g, '-')
          .replace(/-$/, '')
          .replace(/^-/, '');
        
        // Re-encode properly
        const properPath = `/${encodeURIComponent(character)}/${encodeURIComponent(formattedObjectName)}`;
        
        // Only redirect if the path actually changed
        if (properPath !== pathname) {
          return properPath;
        }
      }
    } catch (error) {
      console.warn('Failed to process comma-containing URL:', pathname, error);
    }
  }
  
  return null;
};

// Export specific problematic redirects for debugging
export const commonProblematicRedirects = {
  // Georgian letter "ე" (e)
  '/%25E1%2583%2594': '/%E1%83%94',
  '/%25E1%2583%2594/ეშმაკი': '/%E1%83%94/ეშმაკი',
  '/%25E1%2583%2594/%E1%83%94%E1%83%A8%E1%83%9B%E1%83%90%E1%83%99%E1%83%98': '/%E1%83%94/%E1%83%94%E1%83%A8%E1%83%9B%E1%83%90%E1%83%99%E1%83%98',
  
  // Add more as needed
  '/%25E1%2583%90': '/%E1%83%90', // ა
  '/%25E1%2583%91': '/%E1%83%91', // ბ
  '/%25E1%2583%92': '/%E1%83%92', // გ
  '/%25E1%2583%93': '/%E1%83%93', // დ
  '/%25E1%2583%95': '/%E1%83%95', // ვ
  '/%25E1%2583%96': '/%E1%83%96', // ზ
  '/%25E1%2583%97': '/%E1%83%97', // თ
  '/%25E1%2583%98': '/%E1%83%98', // ი
  '/%25E1%2583%99': '/%E1%83%99', // კ
  '/%25E1%2583%9A': '/%E1%83%9A', // ლ
  '/%25E1%2583%9B': '/%E1%83%9B', // მ
  '/%25E1%2583%9C': '/%E1%83%9C', // ნ
  '/%25E1%2583%9D': '/%E1%83%9D', // ო
  '/%25E1%2583%9E': '/%E1%83%9E', // პ
  '/%25E1%2583%9F': '/%E1%83%9F', // ჟ
  '/%25E1%2583%A0': '/%E1%83%A0', // რ
  '/%25E1%2583%A1': '/%E1%83%A1', // ს
  '/%25E1%2583%A2': '/%E1%83%A2', // ტ
  '/%25E1%2583%A3': '/%E1%83%A3', // უ
  '/%25E1%2583%A4': '/%E1%83%A4', // ფ
  '/%25E1%2583%A5': '/%E1%83%A5', // ქ
  '/%25E1%2583%A6': '/%E1%83%A6', // ღ
  '/%25E1%2583%A7': '/%E1%83%A7', // ყ
  '/%25E1%2583%A8': '/%E1%83%A8', // შ
  '/%25E1%2583%A9': '/%E1%83%A9', // ჩ
  '/%25E1%2583%AA': '/%E1%83%AA', // ც
  '/%25E1%2583%AB': '/%E1%83%AB', // ძ
  '/%25E1%2583%AC': '/%E1%83%AC', // წ
  '/%25E1%2583%AD': '/%E1%83%AD', // ჭ
  '/%25E1%2583%AE': '/%E1%83%AE', // ხ
  '/%25E1%2583%AF': '/%E1%83%AF', // ჯ
  '/%25E1%2583%B0': '/%E1%83%B0', // ჰ
};